import React, {useState} from "react";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import FlexBox from "view/components/Layout/FlexBox";
import {Box, Button, Typography} from "@material-ui/core";
import {apiV2} from "api/firebase";
import {normalizeKey} from "tools/normalizeKey/normalizeKey";
import parseURLParams from "../parseURLParams";
import useStyles from "./styles";

type ResendEmailWithKeyProps = {
  /**
   * The URL still carries a key but the BE rejected it (invalid / expired /
   * legacy). Show the recovery prompt anyway so the reviewer can self-serve a
   * fresh link instead of getting a blank page.
   */
  staleKey?: boolean;
};

/**
 * Stale-URL recovery modal. The reviewer landed without a usable key (the
 * link was cut, expired, or is a legacy pre-FS-2679 link); requests a fresh
 * link to their inbox. Resend needs only `showId` + `reviewerEmail`; the
 * stable `showId` survives the migration and the email comes from the AES
 * `reviewer` param.
 */
function ResendEmailWithKey({staleKey = false}: ResendEmailWithKeyProps) {
  const classes = useStyles();
  const {showId, reviewer, key, taskId, standId} = parseURLParams();

  const normalizedKey = normalizeKey(key);

  // Auto-resend needs showId + the reviewer email (decrypted from the URL).
  const canResend = Boolean(showId && reviewer);

  // Stale key (invalid / expired / legacy): always offer recovery — info-only
  // if we can't auto-resend. Missing key: only when we can actually resend
  // and no valid key is in play.
  const shouldHidePopup = staleKey
    ? !showId
    : !canResend || Boolean(normalizedKey);

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | undefined>();

  if (shouldHidePopup) {
    return null;
  }

  const onClick = async () => {
    if (!showId || !reviewer) return;
    setError(undefined);
    try {
      await apiV2.task.resendTaskEmailKey({
        showId,
        reviewerEmail: reviewer,
        // Forward the dead link's target so the fresh link reopens the SAME
        // task — the shared per-reviewer key would otherwise resolve to an
        // arbitrary task on the BE.
        ...(taskId && standId ? {taskId, standId} : {}),
      });
      setSubmitted(true);
    } catch (e) {
      setError(
        (e as Error)?.message ?? "Couldn't send the new link. Try again."
      );
    }
  };

  return (
    <FfairModal open>
      <FfairPaper className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h2" className={classes.title}>
            Link expired
          </Typography>
        </Box>
        <FlexBox column gap={2} fullWidth>
          <Typography className={classes.content}>
            {submitted &&
              "We've sent a fresh link to your inbox. Open it to access this approval."}
            {!submitted &&
              canResend &&
              "This approval link is no longer valid. Click below and we'll email you a fresh one."}
            {!submitted &&
              !canResend &&
              "This approval link is no longer valid. Please ask the show organiser to resend your task review invitation."}
          </Typography>
          {error && <Typography className={classes.error}>{error}</Typography>}
        </FlexBox>
        {!submitted && canResend && (
          <Box display="flex" justifyContent="flex-end" pt={3}>
            <Button color="primary" variant="contained" onClick={onClick}>
              Resend link
            </Button>
          </Box>
        )}
      </FfairPaper>
    </FfairModal>
  );
}

export default ResendEmailWithKey;
